import {
  isWorkerSuspended,
  SUSPENDED_BADGE_LABEL,
} from '@/util/dependabilityBadge';

export const BACKGROUND_STATUS_MAP: Record<string, string[]> = {
  Approved: ['clear', 'engaged'],
  Declined: ['pre-adverse action', 'post adverse action'],
  Pending: ['report.created', 'needs review'],
};

export const I9_STATUS_MAP: Record<string, string[]> = {
  Pending: ['pending', 'pending_section2', 'prospect'],
  'In Progress': ['in_progress', 'e-verify_in_progress'],
  'Pending E-Verify': ['pending_e-verify'],
  Completed: ['approved', 'n/a'],
};

export const ONBOARDING_STATUS_OPTIONS = [
  { label: 'In Progress', value: 'onboarding_in_progress' },
  { label: 'Pending SSN', value: 'pending_ssn' },
  { label: 'Pending Payment', value: 'pending_payment_method' },
  { label: 'Pending Tax', value: 'pending_tax_withholding' },
  { label: 'Pending Workplace', value: 'pending_workplace' },
  { label: 'Completed', value: 'onboarding_completed' },
];

// GRAVY-10231: the Workers list column + filter are cut over to the v2
// dependability tier scale (monitored/standard/gold/platinum), matching the
// worker profile card. The legacy high/medium/low/not_rated scale is gone from
// this surface. `not_rated` remains only as the neutral fallback the backend
// serves for unscored workers (or when the v2 subsystem is off).
export const DEPENDABILITY_OPTIONS = [
  { label: 'Monitored', value: 'monitored' },
  { label: 'Standard', value: 'standard' },
  { label: 'Gold', value: 'gold' },
  { label: 'Platinum', value: 'platinum' },
];

// Badge status per tier. Badge supports theme/warning/danger/neutral, so the
// two top tiers both map to `theme` (there is no distinct "higher" variant):
//   Monitored -> danger (red), Standard -> neutral (blue), Gold/Platinum -> theme.
// Any unknown value (including the `not_rated` neutral sentinel) falls through
// to neutral via getDependabilityBadgeStatus.
export const DEPENDABILITY_BADGE_STATUS: Record<
  string,
  'theme' | 'warning' | 'danger' | 'neutral'
> = {
  monitored: 'danger',
  standard: 'neutral',
  gold: 'theme',
  platinum: 'theme',
};

// GRAVY-10404: a suspended / permanently-removed worker's tier badge is
// overridden to a distinct dark "Suspended" chip on EVERY surface. The rule
// lives in @/util/dependabilityBadge; both the Workers table and the mobile
// WorkerCard call these two helpers, so threading accountabilityStatus here
// covers both list surfaces at once (display-only — the tier value is untouched).
export const getDependabilityBadgeStatus = (
  tier: string | null | undefined,
  accountabilityStatus?: string | null,
): 'theme' | 'warning' | 'danger' | 'neutral' | 'suspended' => {
  if (isWorkerSuspended(accountabilityStatus)) return 'suspended';
  return (tier && DEPENDABILITY_BADGE_STATUS[tier]) || 'neutral';
};

// Display label for the column badge. A suspended worker reads "Suspended";
// otherwise known tiers use their option label and the neutral sentinel / any
// unknown value are humanized (not_rated -> "Not rated").
export const getDependabilityLabel = (
  tier: string | null | undefined,
  accountabilityStatus?: string | null,
): string => {
  if (isWorkerSuspended(accountabilityStatus)) return SUSPENDED_BADGE_LABEL;
  const value = tier || 'not_rated';
  const opt = DEPENDABILITY_OPTIONS.find((o) => o.value === value);
  if (opt) return opt.label;
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
};

export const SUSPENSION_STATUS_OPTIONS = [
  { label: 'None', value: 'active' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Permanently Removed', value: 'permanently_suspended' },
];

export const TAX_TYPE_OPTIONS = [
  { label: 'W2', value: 'w2' },
  { label: '1099', value: '1099' },
];

export const STATUS_OPTIONS = [
  { label: 'Enabled', value: 'true' },
  { label: 'Disabled', value: 'false' },
];

export const getBackgroundDisplayLabel = (raw: string | null): string => {
  if (!raw) return 'N/A';
  for (const [label, values] of Object.entries(BACKGROUND_STATUS_MAP)) {
    if (values.includes(raw)) return label;
  }
  return raw;
};

export const getI9DisplayLabel = (raw: string | null): string => {
  if (!raw) return 'N/A';
  for (const [label, values] of Object.entries(I9_STATUS_MAP)) {
    if (values.includes(raw)) return label;
  }
  return raw;
};

export const expandBackgroundFilter = (selected: string[]): string[] =>
  selected.flatMap((label) => BACKGROUND_STATUS_MAP[label] || [label]);

export const expandI9Filter = (selected: string[]): string[] =>
  selected.flatMap((label) => I9_STATUS_MAP[label] || [label]);
